import { useState, useEffect, useCallback } from 'react';
import { CheckBox, Popup, Tooltip } from 'devextreme-react';
import { Button, TextBox, TextArea } from 'devextreme-react';
import { useScreenSize } from '../../../utils/media-query';
import { newToast } from '../../../components';
import { ToolbarItem } from 'devextreme-react/popup';
import { UseApiResults } from '../../../api/api.results';
import { useApiFavorites } from '../../../api/api.Favorites';
import { SendResultByEmailMasive, SendResultByEmailMasiveOrder } from '../../../types/types.orders';

interface FavoriteEmail {
  Cf_Id: number;
  Cf_Correo: string;
  Cf_Nombre: string;
}

interface SendMailMasiveProps {
  visible: boolean;
  setVisible: (state: { visible: boolean }) => void;
  dataOrder: SendResultByEmailMasiveOrder[];
  studies?: number[];
  areas?: number[];
}

export const SendMailMasive = ({ visible, setVisible, dataOrder, studies, areas }: SendMailMasiveProps) => {
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const [loading, setLoading] = useState(false);
  const [emailFields, setEmailFields] = useState<{ id: string; value: string }[]>([]);
  const [emailInput, setEmailInput] = useState<string>('');
  const [consolidatePdf, setConsolidatePdf] = useState<boolean>(true);
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const { senResultByMailMasive } = UseApiResults();
  const { GetAllCorreosFavoritos, CreateCorreoFavorito, DeleteCorreoFavorito } = useApiFavorites();

  // Estado para favoritos
  const [favorites, setFavorites] = useState<FavoriteEmail[]>([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showAddFavoritePrompt, setShowAddFavoritePrompt] = useState(false);
  const [pendingEmailToFavorite, setPendingEmailToFavorite] = useState<string>('');
  const [favoriteNameInput, setFavoriteNameInput] = useState<string>('');

  // Cargar favoritos
  const loadFavorites = useCallback(async () => {
    const data = await GetAllCorreosFavoritos();
    setFavorites(data || []);
  }, []);

  useEffect(() => {
    if (visible) {
      loadFavorites();
    }
  }, [visible, loadFavorites]);

  const handleClose = () => {
    setVisible({ visible: false });
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (trimmedEmail && validateEmail(trimmedEmail)) {
      const existingField = emailFields.find((field) => field.value === trimmedEmail);
      if (!existingField) {
        const newEmailField = {
          id: `email_${emailFields.length}_${Date.now()}`,
          value: trimmedEmail,
        };
        setEmailFields([...emailFields, newEmailField]);
        setEmailInput('');

        // Verificar si el correo ya está en favoritos
        const isInFavorites = favorites.some((fav) => fav.Cf_Correo.toLowerCase() === trimmedEmail.toLowerCase());
        if (!isInFavorites) {
          setPendingEmailToFavorite(trimmedEmail);
          setShowAddFavoritePrompt(true);
        }
      } else {
        newToast('WARNING', 'El correo electrónico ya está en la lista.', 5);
      }
    } else {
      newToast('WARNING', 'El correo electrónico no es válido.', 5);
    }
  };

  // Agregar correo desde favoritos a la lista
  const handleAddFromFavorite = (favorite: FavoriteEmail) => {
    const existingField = emailFields.find((field) => field.value === favorite.Cf_Correo);
    if (!existingField) {
      const newEmailField = {
        id: `email_${emailFields.length}_${Date.now()}`,
        value: favorite.Cf_Correo,
      };
      setEmailFields([...emailFields, newEmailField]);
    } else {
      newToast('INFO', 'El correo ya está en la lista de destinatarios.', 3);
    }
  };

  // Eliminar favorito
  const handleDeleteFavorite = async (e: React.MouseEvent, favorite: FavoriteEmail) => {
    e.stopPropagation();
    const success = await DeleteCorreoFavorito(favorite.Cf_Id);
    if (success) {
      newToast('SUCCESS', 'Favorito eliminado correctamente.', 3);
      loadFavorites();
    }
  };

  // Confirmar agregar a favoritos
  const handleConfirmAddToFavorites = async () => {
    if (!favoriteNameInput.trim()) {
      newToast('WARNING', 'Debe ingresar un nombre para el favorito.', 3);
      return;
    }
    if (pendingEmailToFavorite) {
      const name = favoriteNameInput.trim();
      const success = await CreateCorreoFavorito(pendingEmailToFavorite, name);
      if (success) {
        newToast('SUCCESS', 'Correo agregado a favoritos.', 3);
        loadFavorites();
      }
    }
    setShowAddFavoritePrompt(false);
    setPendingEmailToFavorite('');
    setFavoriteNameInput('');
  };

  // Cancelar agregar a favoritos
  const handleCancelAddToFavorites = () => {
    setShowAddFavoritePrompt(false);
    setPendingEmailToFavorite('');
    setFavoriteNameInput('');
  };

  // Función para eliminar un campo de email
  const handleRemoveEmailField = (id: string) => {
    setEmailFields(emailFields.filter((field) => field.id !== id));
  };

  const onSaveClick = async () => {
    if (!dataOrder || dataOrder.length === 0) {
      return;
    }
    if (emailFields.length === 0) {
      newToast('WARNING', 'Debe ingresar al menos un correo de destino', 5);
      return;
    }

    const emailAddresses = emailFields.map((field) => field.value);

    const dataToSend: SendResultByEmailMasive = {
      DataOrder: dataOrder,
      Destinators: emailAddresses,
      SendIndividual: consolidatePdf,
      Studies: studies && studies.length > 0 ? studies : undefined,
      Areas: areas && areas.length > 0 ? areas : undefined,
      Subject: subject.trim() || undefined,
      Body: body.trim() || undefined,
    };

    setLoading(true);
    newToast('INFO', 'Se están enviando los correos electrónicos, esto puede tardar algunos minutos.', 5);

    try {
      await senResultByMailMasive(dataToSend);
    } finally {
      setLoading(false);
    }

    setVisible({ visible: false });
  };

  return (
    <Popup visible={visible} fullScreen={isMedium || isSmall || isXSmall} width={650} maxHeight={'90vh'} title='Envío masivo por correo electrónico' onHiding={handleClose} showCloseButton={true}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Info de órdenes seleccionadas */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '13px', color: '#1565c0' }}>
          <i className='dx-icon-doc' />
          <span>
            Se enviarán resultados de <strong>{dataOrder.length}</strong> orden(es) seleccionada(s)
          </span>
        </div>

        {/* Sección de Favoritos - Colapsable */}
        {favorites.length > 0 && (
          <div>
            <div onClick={() => setShowFavorites(!showFavorites)} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '6px 0', color: '#666', fontSize: '13px' }}>
              <i className={`dx-icon-${showFavorites ? 'chevrondown' : 'chevronright'}`} style={{ marginRight: '6px' }} />
              <i className='dx-icon-favorites' style={{ marginRight: '6px', color: '#ffc107' }} />
              <span>Favoritos ({favorites.length})</span>
            </div>

            {showFavorites && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '6px', backgroundColor: '#f8f9fa', borderRadius: '4px', marginTop: '4px' }}>
                {favorites.map((fav) => {
                  const isAdded = emailFields.some((field) => field.value === fav.Cf_Correo);
                  return (
                    <div
                      key={fav.Cf_Id}
                      onClick={() => !isAdded && handleAddFromFavorite(fav)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '3px 8px',
                        backgroundColor: isAdded ? '#e8f5e9' : '#fff',
                        border: `1px solid ${isAdded ? '#4caf50' : '#ddd'}`,
                        borderRadius: '12px',
                        fontSize: '11px',
                        cursor: isAdded ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: isAdded ? 0.7 : 1,
                        lineHeight: '1.2',
                      }}
                      title={isAdded ? 'Ya agregado' : `Clic para agregar: ${fav.Cf_Correo}`}>
                      {isAdded && <i className='dx-icon-check' style={{ marginRight: '3px', color: '#4caf50', fontSize: '10px' }} />}
                      <span style={{ marginRight: '4px' }}>{fav.Cf_Nombre || fav.Cf_Correo}</span>
                      <i className='dx-icon-trash' onClick={(e) => handleDeleteFavorite(e, fav)} style={{ cursor: 'pointer', color: '#999', fontSize: '10px' }} title='Eliminar de favoritos' />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Input para agregar correo */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TextBox id='emailInputMasive' value={emailInput} onValueChanged={(e) => setEmailInput(e.value)} label='Agregar correo' labelMode='static' placeholder='Escribe un correo' onEnterKey={handleAddEmail} style={{ flex: 1, marginRight: '8px' }} />
          <Button stylingMode='text' icon='add' onClick={handleAddEmail} type='default' />
        </div>

        {/* Prompt para agregar a favoritos */}
        {showAddFavoritePrompt && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#fffde7', borderRadius: '4px', border: '1px solid #fff59d', fontSize: '12px' }}>
            <i className='dx-icon-favorites' style={{ color: '#ffc107', fontSize: '14px' }} />
            <span style={{ color: '#666', whiteSpace: 'nowrap' }}>
              ¿Guardar <strong>{pendingEmailToFavorite}</strong>?
            </span>
            <TextBox value={favoriteNameInput} onValueChanged={(e) => setFavoriteNameInput(e.value)} label='Nombre' labelMode='static' placeholder='Escribe un nombre' style={{ flex: 1 }} />
            <Button icon='check' stylingMode='text' type='success' onClick={handleConfirmAddToFavorites} hint='Agregar a favoritos' />
            <Button icon='close' stylingMode='text' onClick={handleCancelAddToFavorites} hint='Cancelar' />
          </div>
        )}

        {/* Lista de correos agregados */}
        <div className='widget-container'>
          {emailFields.map((field) => (
            <div key={field.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <TextBox value={field.value} readOnly={true} placeholder='Correo electrónico' style={{ flex: 1, marginRight: '8px' }} />
              <Button stylingMode='text' icon='remove' onClick={() => handleRemoveEmailField(field.id)} type='danger' />
            </div>
          ))}
          {emailFields.length > 0 && (
            <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>Los correos electrónicos se enviarán a {emailFields.length} dirección(es).</div>
          )}
        </div>

        {/* Separador */}
        <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

        {/* Envío individual */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }} id='sendIndividualOption'>
          <CheckBox value={consolidatePdf} onValueChanged={(e) => setConsolidatePdf(e.value)} />
          <div>
            <span style={{ fontSize: '13px', fontWeight: 500 }}>Enviar todo en un solo PDF</span>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{consolidatePdf ? 'Todas las órdenes se consolidarán en un único PDF y se enviarán en un solo correo.' : 'Se enviará un solo correo con múltiples archivos PDF adjuntos, uno por cada orden.'}</div>
          </div>
        </div>

        {/* Separador */}
        <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

        {/* Asunto */}
        <TextBox value={subject} onValueChanged={(e) => setSubject(e.value)} label='Asunto' labelMode='static' placeholder='Asunto del correo (opcional)' showClearButton={true} />

        {/* Cuerpo del mensaje */}
        <TextArea value={body} onValueChanged={(e) => setBody(e.value)} label='Mensaje' labelMode='static' placeholder='Escriba el cuerpo del mensaje (opcional)' height={120} />
      </div>

      <ToolbarItem toolbar='bottom' location='after'>
        <div className='form-popup-buttons-container'>
          <Button stylingMode='contained' text='Cancelar' type='normal' onClick={handleClose} />
          <Button stylingMode='contained' text='Enviar' type={loading ? 'normal' : 'default'} disabled={loading} onClick={onSaveClick} />
        </div>
      </ToolbarItem>
    </Popup>
  );
};
